/**
 * React Query hooks for entries
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getEntries,
  getEntry,
  createEntry,
  updateEntry,
  deleteEntry,
  createEntryMedia,
} from '@/api/entries';
import {
  VerifiedEntry,
  CreateEntryRequest,
  UpdateEntryRequest,
  EntrySearchParams,
  CreateEntryMediaRequest,
  EntryMedia,
} from '@/types/api';
import { getErrorMessage } from '@/api/client';

/**
 * Hook to fetch entries with filters
 */
export const useEntries = (params?: EntrySearchParams) => {
  return useQuery<VerifiedEntry[], Error>({
    queryKey: ['entries', params],
    queryFn: () => getEntries(params),
  });
};

/**
 * Hook to fetch single entry
 */
export const useEntry = (id: number) => {
  return useQuery<VerifiedEntry, Error>({
    queryKey: ['entries', id],
    queryFn: () => getEntry(id),
    enabled: !!id,
  });
};

/**
 * Hook to create entry
 */
export const useCreateEntry = () => {
  const queryClient = useQueryClient();

  return useMutation<VerifiedEntry, Error, CreateEntryRequest>({
    mutationFn: createEntry,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['entries'] });
    },
    onError: (error) => {
      console.error('Failed to create entry:', getErrorMessage(error));
    },
  });
};

/**
 * Hook to update entry
 */
export const useUpdateEntry = () => {
  const queryClient = useQueryClient();

  return useMutation<VerifiedEntry, Error, { id: number; data: UpdateEntryRequest }>({
    mutationFn: ({ id, data }) => updateEntry(id, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['entries'] });
      queryClient.invalidateQueries({ queryKey: ['entries', data.id] });
    },
    onError: (error) => {
      console.error('Failed to update entry:', getErrorMessage(error));
    },
  });
};

/**
 * Hook to delete entry
 */
export const useDeleteEntry = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, number>({
    mutationFn: deleteEntry,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['entries'] });
    },
    onError: (error) => {
      console.error('Failed to delete entry:', getErrorMessage(error));
    },
  });
};

/**
 * Hook to link media to entry
 */
export const useCreateEntryMedia = () => {
  const queryClient = useQueryClient();

  return useMutation<
    EntryMedia,
    Error,
    { entryId: number; data: CreateEntryMediaRequest }
  >({
    mutationFn: ({ entryId, data }) => createEntryMedia(entryId, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['entries', variables.entryId] });
    },
    onError: (error) => {
      console.error('Failed to link media:', getErrorMessage(error));
    },
  });
};
